import { VStack, Text, Link } from '@montel/montelpro-shared-components-v2';
import {
	ACCOUNT_API_ASSIGN_AND_MARK_REVIEWED,
	ACCOUNT_API_MARK_REVIEWED,
} from 'apiRoutes';
import { StandardModal } from 'components/common/Modal/StandardModal';
import { MontelAccount } from 'domain/account/interfaces';
import { FunctionComponent } from 'react';
import useSubmit, { POST } from 'hooks/useSubmit';
import { toast } from 'react-toastify';
import { ReviewAndAssignToCompanyFooter } from './ReviewAndAssignToCompanyFooter';
import { UserDetails } from './UserDetails';
import { AssignToCompanyForm } from './AssignToCompanyForm';
import { ReviewFooter } from './ReviewFooter';
import { useAssignToCompanyFormContext } from 'components/companies/AssignToCompanyForm/AssignToCompanyFormContext';

export const ReviewAccountModal: FunctionComponent<{
	showModal: boolean;
	setShowModal: (showModal: boolean) => void;
	onSuccess: (accountId: string, productId: string) => void;
	account?: MontelAccount;
	productId?: string;
	productName?: string;
}> = ({
	showModal,
	setShowModal,
	onSuccess,
	account,
	productId,
	productName,
}) => {
	const [postAssignAndMarkReviewed, postAssignAndMarkReviewedStatus] =
		useSubmit(`${ACCOUNT_API_ASSIGN_AND_MARK_REVIEWED}`, POST);

	const [postMarkReviewed, postMarkReviewedStatus] = useSubmit(
		`${ACCOUNT_API_MARK_REVIEWED}`,
		POST,
	);

	const { selectedCompany, setSelectedCompany } =
		useAssignToCompanyFormContext();

	if (!account || !productId || !productName) return <></>;

	const markReviewed = async () => {
		if (!account || !productId || !productName) return;
		const result = await postMarkReviewed({
			accountId: account.id,
			productId: productId,
		});

		if (result.success) {
			toast.success(
				`${account.email.value} has been marked as reviewed for ${productName}.`,
				{ autoClose: 2000 },
			);
			onCloseModal();
			onSuccess(account.id, productId);
			setSelectedCompany(undefined);
			setShowModal(false);
		} else {
			toast.error('An error occurred, please try again!');
		}
	};

	const assignAndMarkReviewed = async () => {
		if (!account || !productId || !productName || !selectedCompany) return;

		const result = await postAssignAndMarkReviewed({
			accountId: account.id,
			productId: productId,
			companyId: selectedCompany.id,
		});
		if (result.success) {
			toast.success(
				`${account.email.value} has been marked as reviewed for ${productName} and assigned to ${selectedCompany.name}.`,
				{ autoClose: 2000 },
			);
			onCloseModal();
			onSuccess(account.id, productId);
			setSelectedCompany(undefined);
			setShowModal(false);
		} else {
			toast.error('An error occurred, please try again!');
		}
	};

	const onCloseModal = () => {
		setSelectedCompany(undefined);
		setShowModal(false);
	};

	return (
		<StandardModal
			isOpen={showModal}
			onClose={onCloseModal}
			header={
				account.company.id
					? 'Review account'
					: 'Match account to a company'
			}
			footer={
				account.company.id ? (
					<ReviewFooter
						onSubmitted={() => markReviewed()}
						onCloseModal={onCloseModal}
						postMarkReviewedStatus={postMarkReviewedStatus}
					/>
				) : (
					<ReviewAndAssignToCompanyFooter
						onSubmitted={() => assignAndMarkReviewed()}
						onCloseModal={onCloseModal}
						postAssignAndMarkReviewedStatus={
							postAssignAndMarkReviewedStatus
						}
					/>
				)
			}
		>
			<VStack gap="xxl">
				<UserDetails account={account} />
				{account.company.id ? (
					<Text>
						User is already assigned to company{' '}
						<Link href={`/companies/${account.company.id}`}>
							{account.company.name}
						</Link>
						.
					</Text>
				) : (
					<AssignToCompanyForm account={account} />
				)}
			</VStack>
		</StandardModal>
	);
};
